import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton } from '@material-ui/core';
import ReactPlayer from 'react-player/lazy';
import { useDispatch } from 'react-redux';
import { useTranslation } from '../../../../../src/i18n';
import { TP } from '../../../../../constants/consts';
import SubmitModalLayout from '../../../../../components/SubmitModalLayout';
import Button from '../../../../../components/Button';
import DeleteFbIcon from '../../../../../components/Icons/DeleteFbIcon';
import AddFieldWithButton from '../../../../../components/EditComponents/AddFieldWithButton';
import { enqueueSnackbarAction } from '../../../../App/actions';
import UploadFileButton from '../../../../../components/UploadFileButton';
import { getAcceptFormatsTypesForFiles, joinMediaFormats } from '../../../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
    },
  },
  inputField: {
    flex: 1,
  },
  mediaBlock: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
    },
  },
  mediaPlayer: {
    height: 200,
    maxWidth: 400,
    width: '100%',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      flexDirection: 'row',
      marginTop: theme.spacing(1),
      justifyContent: 'space-around',
    },
  },
  orBlock: {
    textTransform: 'uppercase',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const AddVideoModal = ({ isOpened, onClose, uploadVideo, isLoading, formats }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [videoUrl, setVideoUrl] = React.useState('');
  const [videoError, setVideoError] = React.useState(false);
  const [videoSource, setVideoSource] = React.useState(null);

  const clearVideo = () => {
    setVideoError(false);
    setVideoUrl('');
    setVideoSource('');
  };

  const addVideoUrl = url => {
    if (ReactPlayer.canPlay(url)) {
      setVideoSource({ type: 'url', url });
    } else {
      dispatch(
        enqueueSnackbarAction({
          message: t(`${TP}.FN_WEB_LINK_INVALID`),
          options: {
            variant: 'error',
          },
        }),
      );
    }
  };

  return (
    <SubmitModalLayout
      isOpened={isOpened}
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_VIDEO`)}
      title2={t(`${TP}.FN_ACCEPTING_FORMATS`, { formats: joinMediaFormats(formats) })}
      withoutFooter
      disableBackdropClick
      inTop={false}
    >
      <div className={classes.root}>
        <AddFieldWithButton
          id="video_link"
          value={videoUrl}
          onChange={setVideoUrl}
          onAdd={addVideoUrl}
          placeholder={`${TP}.FN_ADD_VIDEO_LINK`}
          label={`${TP}.FN_ADD_VIDEO_LINK_TITLE`}
          autoFocus
          className={classes.inputField}
        />
        <div className={classes.orBlock}>{t(`${TP}.FN_OR`)}</div>
        <UploadFileButton
          accept={getAcceptFormatsTypesForFiles('video', formats)}
          onFilesAdd={files => setVideoSource({ type: 'blob', file: files[0] })}
          className={classes.uploadButton}
        />
      </div>
      <div className={classes.mediaBlock}>
        {videoSource && (
          <>
            <div className={classes.mediaPlayer}>
              <ReactPlayer url={videoSource.url || videoSource?.file?.src} light width="100%" height="100%" controls />
            </div>
            <div className={classes.actions}>
              <IconButton onClick={clearVideo}>
                <DeleteFbIcon />
              </IconButton>
              <Button disabled={videoError} onClick={() => uploadVideo(videoSource)} isLoading={isLoading}>
                {t(`${TP}.m_SAVE`)}
              </Button>
            </div>
          </>
        )}
      </div>
    </SubmitModalLayout>
  );
};

AddVideoModal.propTypes = {
  uploadVideo: PropTypes.func.isRequired,
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  formats: PropTypes.array,
};

export default memo(AddVideoModal);
